<template>
  <section class="product-hub" data-testid="analysis-product-hub">
    <div class="hub-strip">
      <div class="plan-thumb" v-if="imageUrl">
        <img :src="imageUrl" alt="" />
      </div>
      <div class="hub-intent">
        <div class="strip-label">{{ t('analysis.productHub.questionLabel') }}</div>
        <p class="intent-question">{{ hub.question }}</p>
        <p v-if="!requirementsReady" class="intent-answer intent-answer--loading" data-testid="hub-requirements-loading">
          <v-progress-circular indeterminate size="14" width="2" color="primary" />
          <span>{{ t('analysis.productHub.requirementsLoading') }}</span>
        </p>
        <p v-else class="intent-answer">{{ hub.requirementsAnswer }}</p>
      </div>
    </div>

    <div class="product-grid">
      <v-card class="product-card analysis-card" data-testid="product-card-360">
        <v-card-text class="product-card-body">
          <div class="product-card-kicker">{{ t('analysis.productHub.analysis.kicker') }}</div>
          <h2>{{ t('analysis.productHub.analysis.title') }}</h2>
          <p>{{ t('analysis.productHub.analysis.description') }}</p>

          <div class="section-label">{{ t('analysis.productHub.analysis.teaserTitle') }}</div>
          <ul v-if="hub.analysisTeasers.length > 0" class="teaser-list">
            <li v-for="item in hub.analysisTeasers" :key="item">{{ item }}</li>
          </ul>
          <p
            v-if="analysisTeasersStillLoading"
            class="card-loading"
            data-testid="hub-card-360-loading"
            role="status"
            aria-live="polite"
          >
            <v-progress-circular indeterminate size="14" width="2" color="primary" />
            <span>{{ t('analysis.productHub.analysis.loading') }}</span>
          </p>

          <div class="mini-list">
            <span>{{ t('analysis.productHub.analysis.includes.scores') }}</span>
            <span>{{ t('analysis.productHub.analysis.includes.cost') }}</span>
            <span>{{ t('analysis.productHub.analysis.includes.rooms') }}</span>
            <span>{{ t('analysis.productHub.analysis.includes.suggestions') }}</span>
          </div>

          <div class="card-footer">
            <v-btn color="primary" :to="analysisRoute" @click="$emit('open-360')">
              <v-icon icon="mdi-view-dashboard-outline" start />
              {{ t('analysis.productHub.analysis.cta') }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <v-card class="product-card studio-card" data-testid="product-card-studio">
        <v-card-text class="product-card-body">
          <div class="product-card-kicker">{{ t('analysis.productHub.studio.kicker') }}</div>
          <h2>{{ t('analysis.productHub.studio.title') }}</h2>
          <p>{{ t('analysis.productHub.studio.description') }}</p>

          <div class="section-label">{{ t('analysis.productHub.studio.promptTitle') }}</div>
          <ul v-if="hub.studioPrompts.length > 0" class="prompt-list">
            <li v-for="prompt in hub.studioPrompts" :key="prompt.id">
              <v-icon icon="mdi-arrow-right-thin" size="18" />
              <v-btn
                variant="text"
                class="prompt-link"
                :to="studioPromptRoute(prompt)"
                @click="onStudioPromptClick(prompt)"
              >
                {{ prompt.title }}
              </v-btn>
            </li>
          </ul>
          <p
            v-if="studioPromptsStillLoading"
            class="card-loading"
            data-testid="hub-card-studio-loading"
            role="status"
            aria-live="polite"
          >
            <v-progress-circular indeterminate size="14" width="2" color="primary" />
            <span>{{ t('analysis.productHub.studio.loading') }}</span>
          </p>

          <div class="how-it-works">
            {{ t('analysis.productHub.studio.howItWorks') }}
          </div>

          <div class="card-footer">
            <v-btn color="primary" :to="studioRoute" @click="$emit('open-studio')">
              <v-icon icon="mdi-auto-fix" start />
              {{ t('analysis.productHub.studio.cta') }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <OnboardingTour
      tour-id="hub"
      :version="1"
      :auto-show="false"
      :ready="Boolean(hub)"
      :steps="hubTourSteps"
    />
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import OnboardingTour from '@/components/onboarding/OnboardingTour.vue';
import { stashStudioPrefill } from '@/utils/studioPrefillStash.mjs';

const props = defineProps({
  hub: { type: Object, required: true },
  imageUrl: { type: String, default: '' },
  analysisId: { type: String, default: 'current' },
  // Per-section async status from `store.state.analysisSectionStatus`.
  // Values: 'idle' | 'loading' | 'ready'. Hub uses results from rooms,
  // costs, requirements, and studioStarterPack — when any of those are
  // still loading the corresponding card shows a tiny spinner so the
  // user understands "more is coming" instead of seeing a half-empty
  // teaser list and wondering whether they should reload.
  loadingState: {
    type: Object,
    default: () => ({ rooms: 'idle', costs: 'idle', requirements: 'idle', studioStarterPack: 'idle' }),
  },
});

const emit = defineEmits(['open-360', 'open-studio']);

const { t } = useI18n();

// `requirementsAnswer` is empty / fallback while the requirements eval
// is loading. Show a spinner instead of the placeholder copy so the
// user sees the system is actively working.
const requirementsReady = computed(
  () => props.loadingState?.requirements !== 'loading',
);

// 360 card: teaser entries come from costAnalysis (cost level) +
// roomAnalysis (room count) + analysis (strongest/weakest + top move).
// If costs or rooms are still loading the teaser list is incomplete;
// the spinner says so.
const analysisTeasersStillLoading = computed(
  () => props.loadingState?.costs === 'loading' || props.loadingState?.rooms === 'loading',
);

// Studio card: studioPrompts comes from the AI-derived
// studioStarterPack.suggestions (cached) or falls back to
// analysis.suggestions. The spinner fires only while the starter pack
// itself is loading.
const studioPromptsStillLoading = computed(
  () => props.loadingState?.studioStarterPack === 'loading',
);

const hubTourSteps = [
  { icon: 'mdi-home-analytics', tone: 'primary', titleKey: 'onboarding.hub.step1.title', bodyKey: 'onboarding.hub.step1.body' },
  { icon: 'mdi-view-dashboard-outline', tone: 'success', titleKey: 'onboarding.hub.step2.title', bodyKey: 'onboarding.hub.step2.body' },
  { icon: 'mdi-auto-fix', tone: 'studio', titleKey: 'onboarding.hub.step3.title', bodyKey: 'onboarding.hub.step3.body' },
];

const analysisRoute = computed(() => ({
  name: 'Analysis360',
  params: { id: props.analysisId || 'current' },
}));

const studioRoute = computed(() => ({
  name: 'AnalysisStudio',
  params: { id: props.analysisId || 'current' },
}));

function buildStudioPromptPrefill(prompt) {
  if (!prompt) return '';
  return prompt.description?.length
    ? `${prompt.title}. ${prompt.description}`
    : (prompt.title || '');
}

// The directive text (which is the image-gen prompt) is stashed in
// sessionStorage keyed by id; the URL carries only the short id. Studio reads
// the stash back so the full directive survives without a ~1.5 kB query string.
const studioPromptRoute = (prompt) => ({
  name: 'AnalysisStudio',
  params: { id: props.analysisId || 'current' },
  query: {
    suggestion: prompt.id,
    source: `hub_prompt_${prompt.id}`,
  },
});

function onStudioPromptClick(prompt) {
  const prefill = buildStudioPromptPrefill(prompt);
  stashStudioPrefill(prompt.id, prefill);
  emit('open-studio', { promptId: prompt.id, prefill });
}
</script>

<style scoped>
.product-hub {
  display: grid;
  gap: 18px;
}

.hub-strip {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  gap: 18px;
  align-items: center;
  padding: 16px;
  border: 1px solid rgba(41, 70, 86, 0.14);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 12px 32px rgba(24, 39, 48, 0.06);
}

.plan-thumb {
  width: 112px;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 6px;
  background: #edf2f4;
}

.plan-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.strip-label,
.product-card-kicker,
.section-label {
  color: #55717c;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.intent-question {
  margin: 4px 0;
  color: #1f343d;
  font-style: italic;
  font-weight: 650;
}

.intent-answer {
  margin: 0;
  color: #54676f;
}

.intent-answer--loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #55717c;
  font-style: italic;
}

.card-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0 0;
  color: #55717c;
  font-size: 0.86rem;
  font-style: italic;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.product-card {
  border: 1px solid rgba(41, 70, 86, 0.14);
  border-radius: 8px;
  box-shadow: 0 16px 42px rgba(24, 39, 48, 0.08);
}

.product-card-body {
  display: flex;
  min-height: 460px;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
}

.product-card h2 {
  margin: 0;
  color: #172b33;
  font-size: 1.55rem;
  line-height: 1.15;
  overflow-wrap: anywhere;
  hyphens: auto;
}

.product-card p {
  margin: 0;
}

.teaser-list,
.prompt-list {
  display: grid;
  gap: 9px;
  margin: 0;
  padding: 0;
  color: #263f48;
}

.teaser-list {
  padding-left: 18px;
}

.prompt-list {
  list-style: none;
}

.prompt-list li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.prompt-link {
  justify-content: flex-start;
  min-height: 0;
  padding: 0;
  text-transform: none;
  letter-spacing: 0;
  color: #263f48;
  font-weight: 650;
}

.mini-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mini-list span,
.how-it-works {
  border-radius: 6px;
  background: #edf4f1;
  color: #2d554a;
  font-weight: 650;
  padding: 7px 10px;
}

.how-it-works {
  line-height: 1.45;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-top: auto;
}

@media (max-width: 860px) {
  .hub-strip {
    grid-template-columns: 84px minmax(0, 1fr);
  }

  .plan-thumb {
    width: 84px;
  }

  .product-grid {
    grid-template-columns: 1fr;
  }

  .product-card-body {
    min-height: 0;
  }
}

@media (max-width: 600px) {
  .product-card h2 {
    font-size: 1.35rem;
    line-height: 1.18;
  }

  .product-card-body {
    padding: 20px;
  }
}
</style>
